import { useSessionTimeout } from '../hooks/useSessionTimeout'
import { Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Fixed-top banner that warns when the access-token JWT is close to
 * expiring. All chrome strings are localized via the `auth` namespace
 * under `components.sessionTimeoutWarning.*`. The minute-count uses
 * i18next plural keys (`expiringSoon_one` / `_other`) so each locale's
 * CLDR rules apply. The `refresh` action is delegated to the
 * `useSessionTimeout` hook — the API call to `/auth/refresh` is the
 * hook's concern, not this component's.
 */
export function SessionTimeoutWarning() {
  const { t } = useTranslation('auth')
  const { minutesRemaining, isExpiringSoon, isExpiryCritical, refresh } =
    useSessionTimeout()

  if (!isExpiringSoon || minutesRemaining === null) return null

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-3 px-4 py-2 text-sm font-medium ${
        isExpiryCritical
          ? 'bg-red-600 text-white'
          : 'bg-amber-500 text-white'
      }`}
    >
      <Clock className="h-4 w-4 shrink-0" />
      <span>
        {isExpiryCritical
          ? t('components.sessionTimeoutWarning.critical')
          : t('components.sessionTimeoutWarning.expiringSoon', {
              count: minutesRemaining,
            })}
      </span>
      <button
        onClick={refresh}
        className="rounded-md border border-white/30 bg-white/20 px-3 py-1 text-xs font-medium hover:bg-white/30 transition-colors"
      >
        {t('components.sessionTimeoutWarning.extendButton')}
      </button>
    </div>
  )
}
